import type { TFunction } from 'i18next';

import {
  EOnboardingMilestone,
  EOnboardingTaskType,
  EOnboardingTaskTypeClass,
} from '@feathr/blackbox';

export enum EOnboardingProgressiveFeatures {
  // Nav items
  Projects = 'Projects',
  Home = 'Home',
  Community = 'Community',

  // Tasks
  SetupSuperPixel = EOnboardingTaskType.SetupSuperPixel,
  ImportEmailData = EOnboardingTaskType.ImportEmailData,

  // Milestones
  PublishFirstAdCampaign = EOnboardingMilestone.RetargetingCampaign,
  PublishFirstEmailCampaign = EOnboardingMilestone.SingleSendCampaign,
}

export function getRequiredTaskOrMilestone(
  feature: EOnboardingProgressiveFeatures,
): EOnboardingTaskTypeClass | EOnboardingMilestone {
  const map = {
    [EOnboardingProgressiveFeatures.Projects]: EOnboardingTaskTypeClass.SetupBilling,
    [EOnboardingProgressiveFeatures.Home]: EOnboardingTaskTypeClass.SetupBilling,
    [EOnboardingProgressiveFeatures.Community]: EOnboardingTaskTypeClass.SetupSuperPixel,
    [EOnboardingProgressiveFeatures.SetupSuperPixel]: EOnboardingTaskTypeClass.SetupSuperPixel,
    [EOnboardingProgressiveFeatures.ImportEmailData]: EOnboardingTaskTypeClass.ImportEmailData,
    [EOnboardingProgressiveFeatures.PublishFirstAdCampaign]:
      EOnboardingMilestone.InitialAccountSetup,
    [EOnboardingProgressiveFeatures.PublishFirstEmailCampaign]:
      EOnboardingMilestone.InitialAccountSetup,
  } as const;

  return map[feature];
}

export function isTask(
  query: EOnboardingTaskTypeClass | EOnboardingMilestone,
): query is EOnboardingTaskTypeClass {
  return Object.values(EOnboardingTaskTypeClass).includes(query as EOnboardingTaskTypeClass);
}

export function isMilestone(
  query: EOnboardingTaskTypeClass | EOnboardingMilestone,
): query is EOnboardingMilestone {
  return Object.values(EOnboardingMilestone).includes(query as EOnboardingMilestone);
}

export function getMessage(feature: EOnboardingProgressiveFeatures, t: TFunction): string {
  const map: Record<EOnboardingProgressiveFeatures, string> = {
    [EOnboardingProgressiveFeatures.Projects]: t(
      'Complete your billing setup to unlock Projects and start creating marketing campaigns.',
    ),
    [EOnboardingProgressiveFeatures.Home]: t(
      'Complete the "Setup your Super Pixel" task to unlock Home and start creating marketing campaigns.',
    ),
    [EOnboardingProgressiveFeatures.Community]: t(
      'Complete the "Setup your Super Pixel" setup task to unlock the Community page.',
    ),
    [EOnboardingProgressiveFeatures.SetupSuperPixel]: t(
      'Complete the "Setup your Super Pixel" task to unlock this task.',
    ),
    [EOnboardingProgressiveFeatures.ImportEmailData]: t(
      'Complete the "Import Email Data" task to proceed with email campaign creation.',
    ),
    [EOnboardingProgressiveFeatures.PublishFirstAdCampaign]: t(
      'Complete the "Set up your account" milestone to create your first ad campaign.',
    ),
    [EOnboardingProgressiveFeatures.PublishFirstEmailCampaign]: t(
      'Complete the "Set up your account" milestone to create your first email campaign.',
    ),
  } as const;

  return map[feature];
}
